import { useEffect, useRef, useState, useCallback } from 'react';
import properties from '../../properties';

type WSMessage =
  | { status: 'processing'; progress: number }
  | { status: 'completed'; fileName: string }
  | { status: 'error'; message: string };

export const useWebSocket = (userId: string) => {
  const wsRef = useRef<WebSocket | null>(null);
  const shouldReconnect = useRef(true);
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelay = useRef(2000);

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WSMessage | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const connect = useCallback(() => {
    if (!userId) {
      return;
    }

    const ws = new WebSocket(`${properties.websocketURL}?clientId=${userId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('✅ WebSocket connected');
      setIsConnected(true);
      reconnectDelay.current = 2000;
    };

    ws.onmessage = (event) => {
      try {
        const data: WSMessage = JSON.parse(event.data);
        setLastMessage(data);

        if (data.status === 'processing') {
          setProgress(data.progress);
        }

        if (data.status === 'completed') {
          console.log('🎉 Extraction completed:', data);
          setProgress(null);
        }

        if (data.status === 'error') {
          console.error('Processing error:', data.message);
          setProgress(null);
        }
      } catch (err) {
        console.error('Invalid WS message', err);
      }
    };

    ws.onclose = () => {
      console.log('❌ WebSocket disconnected');
      setIsConnected(false);

      if (shouldReconnect.current) {
        reconnectTimeout.current = setTimeout(() => {
          reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000);
          connect();
        }, reconnectDelay.current);
      }
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
      ws.close();
    };
  }, [userId]);

  useEffect(() => {
    shouldReconnect.current = true;
    connect();

    return () => {
      shouldReconnect.current = false;
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      wsRef.current?.close();
    };
  }, [connect]);

  return { isConnected, lastMessage, progress };
};
